import { useApolloClient } from '@apollo/react-hooks';
import { useLocation } from 'react-router';
import { SYNC_SUCCESS_SUBSCRIPTION } from 'api/graphql/v2/subscriptions/Events';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import type { Observable } from 'zen-observable-ts';
import {
  ROUTE_PARAMS,
  ROUTE_PATHS,
  TARGET_BLANK
} from 'utils/constants/routes';
import { FEATURES, getFeatureAvailability } from 'utils/featureToggle';
import { limitProviderName } from 'utils/constants/toastMessage';
import { isPlannerRoute } from 'helpers/routeHelper';
import { SyncNotice, isCentralReachDownStream } from './ToastUtility';
import ToastMessage from './ToastMessage';

// Minimal structural type — we only need unsubscribe().
type ZenSubscription = { unsubscribe: () => void };

const SUCCESS_STATUS_MAPPINGS: Record<string, string> = {
  CREATE_SUCCESS: 'Created',
  UPDATE_SUCCESS: 'Updated',
  CANCEL_SUCCESS: 'Cancelled'
};

const notify = (successNotice: SyncNotice | null | undefined) => {
  console.log('notify event syncsuccesstoast', successNotice);
  if (!successNotice) return;

  // Suppress Central Reach sync toasts when CR status is hidden.
  if (
    getFeatureAvailability(FEATURES.CR_SYNC_DISABLED) &&
    isCentralReachDownStream(successNotice.downStream)
  ) {
    return;
  }

  const { eventId, downStream, isSupressLink } = successNotice;
  const noticeId = `${eventId}-${downStream}`;
  const link = ROUTE_PATHS.VIEW_APPOINTMENT + ROUTE_PARAMS.EVENT + eventId;

  const onToastClick = () => {
    if (!isSupressLink) window.open(link, TARGET_BLANK);
  };

  toast.success(syncNoticeContent(successNotice), {
    autoClose: 10000,
    toastId: noticeId,
    containerId: noticeId,
    closeOnClick: !isSupressLink,
    onClick: onToastClick
  });
};

const syncNoticeContent = (successNotice: SyncNotice) => {
  console.log('syncNoticeContent', successNotice);
  const { providerName, downStream, eventId } = successNotice;
  const time = new Date(successNotice.startDate!).toLocaleTimeString(
    navigator.language,
    {
      hour: '2-digit',
      minute: '2-digit'
    }
  );
  const action = SUCCESS_STATUS_MAPPINGS[successNotice.status!];

  const message = `${limitProviderName(
    providerName!
  )}'s ${time} event was successfully ${action} in ${downStream}`;

  const onClose = (e: any) => {
    const toastId = `${eventId}-${downStream}`;
    e.stopPropagation();
    toast.dismiss(toastId);
  };
  console.log('syncNoticeContent message', message);
  return <ToastMessage message={message} onClose={onClose} />;
};

type ApolloLike = {
  subscribe: (options: { query: any; variables?: any }) => Observable<any>;
};

let refCount = 0;
let activeSub: ZenSubscription | null = null;
let activeCreatedBy: string | undefined;
let teardownTimer: ReturnType<typeof setTimeout> | null = null;

const startSubscription = (client: ApolloLike, createdBy: string) => {
  if (teardownTimer !== null) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }
  if (activeSub && activeCreatedBy === createdBy) {
    return;
  }
  if (activeSub && activeCreatedBy !== createdBy) {
    // CreatedBy changed (rare — account swap). Stop the old operation so
    // we don't leave a dangling server row.
    activeSub.unsubscribe();
    activeSub = null;
  }

  console.log(
    '[SyncSuccessToast] starting singleton subscription for CreatedBy:',
    createdBy
  );
  activeCreatedBy = createdBy;
  activeSub = client
    .subscribe({
      query: SYNC_SUCCESS_SUBSCRIPTION,
      variables: { CreatedBy: createdBy }
    })
    .subscribe({
      next: ({
        data
      }: {
        data?: { eventSyncSuccessNotice?: SyncNotice };
      }) => {
        const successNotice = data?.eventSyncSuccessNotice;
        if (!successNotice) return;
        // No publish-time route gate needed — the subscription itself is
        // only alive while the user is on the Planner (see effect below).
        notify(successNotice);
      },
      error: (err: any) => {
        console.error('[SyncSuccessToast] subscription error:', err);
      }
    });
};

const scheduleTeardown = () => {
  if (teardownTimer !== null) return;
  teardownTimer = setTimeout(() => {
    teardownTimer = null;
    if (refCount > 0) return;
    console.log('[SyncSuccessToast] tearing down singleton subscription');
    activeSub?.unsubscribe();
    activeSub = null;
    activeCreatedBy = undefined;
  }, 0);
};

interface Props {
  account: any;
}

const SyncSuccessToast: React.FC<Props> = ({ account }: Props) => {
  const location = useLocation();
  const onPlannerPage = isPlannerRoute(location?.pathname);
  const client = (useApolloClient() as unknown) as ApolloLike;
  const createdBy: string | undefined = account?.name;

  useEffect(() => {
    if (!getFeatureAvailability(FEATURES.SUCCESS_NOTICE_SUBSCRIPTIONS)) {
      return;
    }
    if (!createdBy) return;
    if (!onPlannerPage) return;

    refCount += 1;
    startSubscription(client, createdBy);
    return () => {
      refCount = Math.max(0, refCount - 1);
      if (refCount === 0) {
        scheduleTeardown();
      }
    };
  }, [client, createdBy, onPlannerPage]);

  return <></>;
};

export default SyncSuccessToast;
